import { Inject, Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { User, UserEncounterCount, UserHeader, UserInsert, UserUpdate } from "../models/user";
import { Observable, catchError, lastValueFrom, map, of } from "rxjs";
import { APINotFoundException } from "../models/errors";
import { MessageService } from "primeng/api";
import { plainToClass, plainToInstance } from "class-transformer";
import { Store } from "@ngxs/store";
import { Users } from "../state/user/user-actions";
import { BASE_URL } from "./injection-tokens";



export abstract class UserServiceBase
{
  abstract getMe() : Observable<User>
  abstract getSetting<T>(settingName: string) : Promise<T>;
  abstract setSetting<T>(settingName: string, value: T) : Promise<void>;
  abstract getActiveUsers() : Promise<UserHeader[]>;
  abstract getAllUsers(): Promise<User[]>;
  abstract getUser(userId: number) : Promise<User>;
  abstract insertUser(user: UserInsert) : Promise<User>;
  abstract updateUser(userId: number, user: UserUpdate) : Promise<User>;
  abstract deleteUser(userId: number): Promise<User>;
  abstract getUserEncounterCounts() : Promise<UserEncounterCount[]>;
}

@Injectable({
    providedIn: 'root'
  })
  export class UserService extends UserServiceBase
  {

    constructor
    (
      private _api: ApiService,      
      private _messages: MessageService,
      private _store: Store,
      @Inject(BASE_URL) private _baseUri: string,
    )
    {
      super()
    }

    override getActiveUsers(): Promise<UserHeader[]> 
    {
      return lastValueFrom
      (
         this._api.makeAPIGetCall<UserHeader[]>(this._baseUri,"/util/users",{})
          .pipe
          (
            map
            (
              users => users.map(u => plainToClass(UserHeader,u))

            )
          )
      );
    }

    override getAllUsers(): Promise<User[]>
    {
      return lastValueFrom
      (
        this._api.makeAPIGetCall<User[]>(this._baseUri, "/util/users/details", {})
          .pipe
          (
            map
            (
              users => users.map(u => plainToClass(User, u))
            )
          )
      );
    }


    override getSetting<T>(settingName: string): Promise<T> 
    {
      return lastValueFrom(this._api.makeAPIGetCall<T>(this._baseUri,"/util/user/me/setting/"+settingName,{},{})
              .pipe
              (
                //This is okay... 
                catchError((error:APINotFoundException) => {return of(undefined as T)})
              ));
    }

    override setSetting<T>(settingName: string, value: T): Promise<void> 
    {
      return lastValueFrom
      (
        this._api.makeAPIPutCall<void>(this._baseUri,"/util/user/me/setting/"+settingName,value,{})
      );
    }

    override getUser(userId: number): Promise<User> 
    {
      return lastValueFrom(this._api.makeAPIGetCall<User>(this._baseUri,"/util/user/"+userId.toString(),{}));
    }

    override getMe() : Observable<User>
    {
        return this._api.makeAPIGetCall<User>(this._baseUri,"/util/user/me",{},{});    
    }

    override insertUser(user: UserInsert): Promise<User>
    {
      return lastValueFrom(this._api.makeAPIPutCall<User>(this._baseUri, "/util/users/", user, {})
      .pipe
        (
          map
          (
            p =>
            {
              this._messages.add({ key: "main-alert", severity: "success", summary: "Success", detail: "Inserted User " + p.fullName});
              this._store.dispatch(new Users.UserInserted({user: p}))
              return p;
            }
          )
        )
      );
    }

    override updateUser(userId: number, userUpdate: UserUpdate): Promise<User>
    {
      return lastValueFrom(this._api.makeAPIPatchCall<User>(this._baseUri, "/util/user/"+userId.toString(), userUpdate, {})
      .pipe
        (
          map
          (
            p =>
            {
              this._messages.add({ key: "main-alert", severity: "success", summary: "Success", detail: "Updated User " + p.fullName});
              this._store.dispatch(new Users.UserUpdated({user: p}))
              return plainToInstance(User,p);
            }
          )
        )
      );
    }

    override deleteUser(userId: number): Promise<User>
    {
      return lastValueFrom(this._api.makeAPIDeleteCall<User>(this._baseUri, "/util/user/"+userId.toString(), {}, {})
      .pipe
        (
          map
          (
            p =>
            {
              this._messages.add({ key: "main-alert", severity: "success", summary: "Success", detail: "Deleted User " + p.fullName});
              this._store.dispatch(new Users.UserDeleted({user: p}))
              return p;
            }
          )
        )
      );
    }

    override getUserEncounterCounts(): Promise<UserEncounterCount[]> 
    {
        return lastValueFrom(this ._api.makeAPIGetCall<UserEncounterCount[]>(this._baseUri, "/util/user/encounter-counts",{}));    
    }
  }
